/**
 * Command-based list components for ContextLinkingPopover and ContextLinkingAutocomplete.
 * These display sessions/patients in a Command (combobox) interface.
 */

import { type LinkedPatientContext, type LinkedSessionContext } from '@/atoms/chatAtoms';
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@heidi/design-system/command';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useQueryAdminSessions } from '@/hooks/query/useQuerySessionsV2';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, FileText, UserRound } from 'lucide-react';
import { useMemo } from 'react';

// ============================================================================
// Session List Sub-component (Command variant)
// ============================================================================

interface SessionListProps {
  searchQuery: string;
  onSelect: (session: LinkedSessionContext) => void;
  /** Maximum number of items to display. Default: 20 */
  maxItems?: number;
  /** Whether to show the "Load more" option. Default: true */
  showLoadMore?: boolean;
  /** Whether to show the group heading. Default: true */
  showHeading?: boolean;
}

export const SessionList = ({
  searchQuery,
  onSelect,
  maxItems = 20,
  showLoadMore = true,
  showHeading = true,
}: SessionListProps) => {
  const { t } = useTranslations();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useQueryAdminSessions();

  // Flatten all pages of sessions
  const allSessions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.sessions);
  }, [data?.pages]);

  // Filter sessions by search query
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return allSessions.slice(0, maxItems);

    const query = searchQuery.toLowerCase();
    return allSessions
      .filter(
        (session) =>
          session.session_name?.toLowerCase().includes(query) ||
          session.patient?.name?.toLowerCase().includes(query)
      )
      .slice(0, maxItems);
  }, [allSessions, searchQuery, maxItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader size={20} />
      </div>
    );
  }

  if (filteredSessions.length === 0) {
    return (
      <CommandEmpty>
        {t('chat.contextLinking.noSessionsFound' as TranslationKey)}
      </CommandEmpty>
    );
  }

  return (
    <CommandGroup
      heading={showHeading ? t('chat.contextLinking.sessions' as TranslationKey) : undefined}
    >
      {filteredSessions.map((session) => (
        <CommandItem
          key={session.session_id}
          value={session.session_id}
          onSelect={() =>
            onSelect({
              id: session.session_id,
              name:
                session.session_name ||
                t('chat.contextLinking.untitledSession' as TranslationKey),
              patientName: session.patient?.name ?? undefined,
              createdAt: session.created_time_local ?? undefined,
            })
          }
          className="flex flex-col items-start gap-0.5 py-2"
        >
          <div className="flex w-full items-center gap-2">
            <FileText size={14} className="shrink-0 text-text-secondary" />
            <TypographyP2 className="flex-1 truncate">
              {session.session_name ||
                t('chat.contextLinking.untitledSession' as TranslationKey)}
            </TypographyP2>
          </div>
          <div className="flex w-full items-center gap-3 ps-5">
            {session.patient?.name && (
              <TypographyCaption className="flex items-center gap-1 text-text-tertiary">
                <UserRound size={10} />
                {session.patient.name}
              </TypographyCaption>
            )}
            {session.created_time_local && (
              <TypographyCaption className="flex items-center gap-1 text-text-tertiary">
                <Calendar size={10} />
                {formatDistanceToNow(new Date(session.created_time_local), {
                  addSuffix: true,
                })}
              </TypographyCaption>
            )}
          </div>
        </CommandItem>
      ))}
      {showLoadMore && hasNextPage && (
        <CommandItem
          onSelect={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="justify-center text-text-secondary"
        >
          {isFetchingNextPage ? (
            <Loader size={14} />
          ) : (
            t('chat.contextLinking.loadMore' as TranslationKey)
          )}
        </CommandItem>
      )}
    </CommandGroup>
  );
};

// ============================================================================
// Patient List Sub-component (Command variant)
// ============================================================================

interface PatientListProps {
  searchQuery: string;
  onSelect: (patient: LinkedPatientContext) => void;
  /** Maximum number of items to display. Default: 20 */
  maxItems?: number;
  /** Whether to show the group heading. Default: true */
  showHeading?: boolean;
}

export const PatientList = ({
  searchQuery,
  onSelect,
  maxItems = 20,
  showHeading = true,
}: PatientListProps) => {
  const { t } = useTranslations();
  const { data: patients, isLoading } = useQueryPatientProfiles();

  // Filter patients by search query
  const filteredPatients = useMemo(() => {
    if (!patients) return [];
    const patientsWithId = patients.filter((p) => p.id);
    if (!searchQuery.trim()) return patientsWithId.slice(0, maxItems);

    const query = searchQuery.toLowerCase();
    return patientsWithId
      .filter(
        (patient) =>
          patient.first_name?.toLowerCase().includes(query) ||
          patient.last_name?.toLowerCase().includes(query) ||
          patient.demographic_string?.toLowerCase().includes(query)
      )
      .slice(0, maxItems);
  }, [patients, searchQuery, maxItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader size={20} />
      </div>
    );
  }

  if (filteredPatients.length === 0) {
    return (
      <CommandEmpty>
        {t('chat.contextLinking.noPatientsFound' as TranslationKey)}
      </CommandEmpty>
    );
  }

  return (
    <CommandGroup
      heading={showHeading ? t('chat.contextLinking.patients' as TranslationKey) : undefined}
    >
      {filteredPatients.map((patient) => {
        const fullName = [patient.first_name, patient.last_name]
          .filter(Boolean)
          .join(' ');
        const displayName =
          fullName ||
          patient.demographic_string ||
          t('chat.contextLinking.unknownPatient' as TranslationKey);
        // Only show demographic string as subtitle when it's not already the display name
        const showDemographicString = fullName && patient.demographic_string;

        return (
          <CommandItem
            key={patient.id}
            value={patient.id ?? undefined}
            onSelect={() =>
              onSelect({
                id: patient.id!,
                name: displayName,
                demographicString: showDemographicString
                  ? patient.demographic_string
                  : undefined,
              })
            }
            className="flex flex-col items-start gap-0.5 py-2"
          >
            <div className="flex w-full items-center gap-2">
              <UserRound size={14} className="shrink-0 text-text-secondary" />
              <TypographyP2 className="flex-1 truncate">{displayName}</TypographyP2>
            </div>
            {showDemographicString && (
              <TypographyCaption className="ps-5 text-text-tertiary">
                {patient.demographic_string}
              </TypographyCaption>
            )}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
};
